/**
 * openAlert — what a tap on an alert does, in one place (bellstack, 3 Oct 2026).
 *
 * The phone's Alerts and the /notifications page used to open an alert each
 * their own way, and only the phone learnt to stand in the business first. Both
 * now come here, so the two cannot drift apart again.
 *
 * @since 1.0.0 (bellstack)
 */
import { useAuth } from '@/shared/hooks/useAuth';
import { goToFix } from '@/shared/errors/goToFix';
import type { Notification } from '@/shared/components/NotificationCenter';
import type { AlertStack } from './alertStacks';

/** Stand in a business's books before landing on its page: a plain switch when
 *  you hold a seat there, else the office's Enter Client door (openClientAt). */
export function standIn(orgId: string, name: string): void {
    const { orgs, currentOrg, switchOrg, enterClientOrg } = useAuth.getState();
    if (currentOrg?.id === orgId) return;
    if (orgs.some((o) => o.id === orgId)) switchOrg(orgId);
    else enterClientOrg(orgId, name);
}

/**
 * Open one alert, and the twins its line stands for. A challan work-task
 * persists until the money lands: opening it must NOT dismiss it, or a real
 * challan vanishes on a stray tap. A sticky item (a pending invitation) is the
 * same: it clears only by being accepted, cancelled, or expiring.
 *
 * The landing goes through goToFix, which splits path, query and hash: "Nobody
 * handles ITR-3" lands on `/portfolio#team?kind=ITR-3`, and a plain router
 * navigate to that string treats the whole of it as the path.
 *
 * Returns true when it left for a page. On a phone the Karr Phone is a sheet
 * over the whole page, so the caller gets out of the way (phonetapgo, 8 Oct
 * 2026): the page opened underneath and nobody saw it.
 */
export function openAlert(n: Notification, ids: string[], markRead: (id: string) => void): boolean {
    if (!n.id.startsWith('ch:') && !n.sticky) ids.forEach((id) => markRead(id));
    if (!n.actionHref) return false;
    if (n.standIn && n.orgId) standIn(n.orgId, n.orgName ?? '');
    void goToFix(n.actionHref);
    return true;
}

/** A stack's head lands on its own page, in its business when every line
 *  shares one. True when it left for a page, as openAlert. */
export function openStackHead(st: AlertStack): boolean {
    if (!st.href) return false;
    const first = st.lines[0].n;
    if (first.standIn && first.orgId && st.lines.every((l) => l.n.orgId === first.orgId)) standIn(first.orgId, first.orgName ?? '');
    void goToFix(st.href);
    return true;
}
